import { cn } from '@/lib/utils'
import { Switch } from '../ui/switch'

// Full-width segment pill switch (Pilot Flying|Pilot Monitoring, Day|Night,
// Airframe|Engine, Deferred|Action): grey rounded track, selected segment is
// a black pill.
export function SegmentedSwitch<T extends string>({
  options,
  value,
  onChange,
  labels,
  icons,
  readOnly,
  error,
  className,
}: {
  options: readonly T[]
  value: T | null
  onChange: (next: T) => void
  labels?: Partial<Record<T, string>>
  icons?: Partial<Record<T, React.ReactNode>>
  readOnly?: boolean
  error?: boolean
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex w-full gap-1 rounded-full bg-surface-L2 p-1',
        error && 'ring-1 ring-border-negative-bold',
        className
      )}
    >
      {options.map((option) => {
        const selected = value === option
        return (
          <button
            key={option}
            type="button"
            disabled={readOnly}
            onClick={() => onChange(option)}
            className={cn(
              'flex min-h-9 flex-1 items-center justify-center gap-1.5 rounded-full px-3 text-sm font-semibold transition-colors',
              selected
                ? 'bg-content-primary-bold text-content-primary-inverse'
                : 'text-content-primary-subtle',
              readOnly && 'pointer-events-none'
            )}
          >
            {icons?.[option] && <span className="[&>svg]:size-4">{icons[option]}</span>}
            {labels?.[option] ?? option}
          </button>
        )
      })}
    </div>
  )
}

// Conditional-section toggle row ("De-icing performed this leg?" / "Any
// defects to report?"): bordered card row with an iOS-style switch.
export function RichToggleRow({
  title,
  checked,
  onChange,
  readOnly,
}: {
  title: string
  checked: boolean
  onChange: (next: boolean) => void
  readOnly?: boolean
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-border-L2 px-3 py-2.5">
      <p className="text-sm font-semibold text-content-primary-bold">{title}</p>
      <Switch checked={checked} onCheckedChange={onChange} disabled={readOnly} />
    </div>
  )
}
